import React from "react";
import { Download, Trash2, FolderInput, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { fileUrl } from "@/lib/api";

export default function Lightbox({ open, onClose, image, onDownload, onMove, onDelete }) {
  if (!image) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-[min(95vw,1400px)] max-h-[95vh] p-0 rounded-sm overflow-hidden bg-zinc-950 border-zinc-800"
        data-testid="lightbox-dialog"
      >
        <DialogTitle className="sr-only">Image preview: {image.keyword}</DialogTitle>
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-sm bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center text-white"
          data-testid="lightbox-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="grid lg:grid-cols-[1fr_320px] max-h-[95vh]">
          {/* Image panel */}
          <div className="bg-zinc-950 flex items-center justify-center p-4 overflow-auto">
            <img
              src={fileUrl(image.url)}
              alt={image.keyword}
              className="max-w-full max-h-[85vh] object-contain"
              data-testid="lightbox-image"
            />
          </div>

          {/* Sidebar metadata */}
          <aside className="bg-white p-6 overflow-y-auto border-l border-zinc-200">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
              Creative
            </div>
            <h3 className="font-heading text-xl font-bold text-zinc-900 mt-1 break-words">
              {image.keyword}
            </h3>

            <div className="mt-6 space-y-4 text-sm">
              <Field label="Style" value={image.style} mono />
              <Field label="Variation" value={`#${image.variation_number}`} mono />
              {image.demographic && <Field label="Demographic" value={image.demographic} />}
              {image.ethnicity && <Field label="Ethnicity / look" value={image.ethnicity} />}
              {image.language && <Field label="Language" value={image.language.toUpperCase()} mono />}
              <Field label="Headline" value={image.headline} />
              <Field label="CTA" value={image.cta} />
              <Field label="Cost" value={`$${(image.cost_usd ?? 0).toFixed(3)}`} mono />
              <Field label="Filename" value={image.filename} mono small />
            </div>

            <div className="mt-8 space-y-2">
              <Button
                onClick={() => onDownload?.(image)}
                className="w-full rounded-sm bg-[#0055FF] hover:bg-[#0040CC]"
                data-testid="lightbox-download"
              >
                <Download className="w-4 h-4 mr-2" /> Download PNG
              </Button>
              {onMove && (
                <Button
                  onClick={() => onMove?.(image)}
                  variant="outline"
                  className="w-full rounded-sm"
                  data-testid="lightbox-move"
                >
                  <FolderInput className="w-4 h-4 mr-2" /> Move to folder
                </Button>
              )}
              {onDelete && (
                <Button
                  onClick={() => onDelete?.(image)}
                  variant="outline"
                  className="w-full rounded-sm text-red-700 border-red-200 hover:bg-red-50 hover:border-red-300"
                  data-testid="lightbox-delete"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </Button>
              )}
            </div>
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, mono = false, small = false }) {
  if (!value) return null;
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{label}</div>
      <div className={`mt-0.5 text-zinc-900 ${mono ? "font-mono" : ""} ${small ? "text-xs break-all" : ""}`}>
        {value}
      </div>
    </div>
  );
}
